import styled from "@emotion/styled";
import { KeyboardEvent, useRef, ReactNode } from "react";
import { Controller, useFormContext, ControllerRenderProps, FieldValues } from "react-hook-form";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { VisibleFocusStyle } from "@kaltura/ds-react-theme";
import { FormLabel } from "../../form";
import { FormHelperText } from "@kaltura/ds-react-components";

/**
 * data structure for radio cards
 */
export interface Card {
    /**
     * card field id
     */
    id: string;
    /**
     * card name. used in analytics.
     */
    name: string;
    /**
     * the card content
     */
    content: ReactNode;
    /**
     * aria label to be used when no other text label exists
     */
    ariaLabel?: string;
    /**
     * aria labelled by to be used when an external label exist
     */
    ariaLabelledBy?: string;
    /**
     * optional,external card label
     */
    label?: ReactNode;
}

interface CardRadioProps {
    className?: string;
    id: string;
    label: string;
    description?: string;
    cards: Card[];
    value?: string;
}

const StyledLabel = styled(FormLabel)(({ theme }) => ({
    marginBottom: theme.spacing(1),
    display: "block",
}));

const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const StyledGrid = styled("div")<{ singleItem?: boolean }>(({ theme, singleItem }) => ({
    display: "grid",
    gridTemplateColumns: singleItem ? "1fr" : "1fr 1fr",
    gap: theme.spacing(1),
    width: "100%",
}));

const StyledCardItemWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    rowGap: theme.spacing(1),
}));

const StyledCardItem = styled(
    Box,
    {
        shouldForwardProp: (prop) => prop !== "isSelected",
    }
)<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
    display: "flex",
    flexBasis: "100%",
    borderRadius: theme.kaltura.shape.roundness2,
    cursor: "pointer",
    border: `2px solid ${isSelected ? theme.palette.primary.main : "transparent"}`, // Double border technique: outer border for selection state (2px)
    boxShadow: isSelected ? "none" : `inset 0 0 0 1px ${theme.kaltura.palette.tone4}`, // inner border (via boxShadow) for constant outline (1px)
    "&:focus-visible": VisibleFocusStyle,
    padding: 0,
    gap: theme.spacing(0.5),
    overflow: "hidden",
    "& svg": {
        minWidth: "90px",
    },
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        flexDirection: "column",
        alignItems: "center",
        "& svg": {
            minWidth: "72px",
        },
    },
}));

/**
 * Cards component - provides an accessible grid of cards for townhall creation
 */
export const CardRadio = (props: CardRadioProps) => {
    const { className = "", id, value, label, description, cards } = props;
    const { control } = useFormContext();
    const cardRefs = useRef(new Map<string, HTMLDivElement>());
    const cardIds = cards.map((card) => card.id);

    const sendButtonAnalytics = useButtonAnalytics();

    const handleCardClick = (card: Card, field: ControllerRenderProps<FieldValues, string>) => {
        sendButtonAnalytics(`${card.name}`, ButtonClickAnalyticsType.TOGGLE); // analytics context provides full event name
        field.onChange(card.id);
    };

    const handleCardKeyDown = (
        e: KeyboardEvent<HTMLDivElement>,
        index: number,
        field: ControllerRenderProps<FieldValues, string>
    ) => {
        let nextIndex: number;
        switch (e.key) {
            case "ArrowRight":
            case "ArrowDown":
                e.preventDefault();
                nextIndex = (index + 1) % cardIds.length;
                break;
            case "ArrowLeft":
            case "ArrowUp":
                e.preventDefault();
                nextIndex = (index - 1 + cardIds.length) % cardIds.length;
                break;
            case "Enter": {
                e.preventDefault();
                // Find the closest form and submit it
                const form = e.currentTarget.closest("form");
                if (form) {
                    form.requestSubmit();
                }
                return;
            }
            default:
                return;
        }
        const nextCard = cards[nextIndex];
        cardRefs.current.get(nextCard.id)?.focus();
        sendButtonAnalytics(nextCard.name, ButtonClickAnalyticsType.TOGGLE); // analytics context provides full event name
        field.onChange(nextCard.id);
    };

    return (
        <div className={className}>
            <StyledLabel text={label} fontWeightBold={true} id={`${id}-label`} />
            {description && <StyledFormHelperText>{description}</StyledFormHelperText>}
            <Controller
                name={id}
                control={control}
                defaultValue={value || cardIds[0] || ""}
                render={({ field }) => (
                    <StyledGrid
                        className="kms-ds-radio-cards"
                        role="radiogroup"
                        aria-labelledby={`${id}-label`}
                        singleItem={cards.length === 1}
                    >
                        {cards.map((card: Card, index: number) => (
                            <StyledCardItemWrapper key={card.id}>
                                <StyledCardItem
                                    key={card.id}
                                    ref={(element: HTMLDivElement | null) => {
                                        if (element) {
                                            cardRefs.current.set(card.id, element);
                                        }
                                    }}
                                    isSelected={cards.length > 1 && field.value === card.id}
                                    onClick={() => handleCardClick(card, field)}
                                    onKeyDown={(e) => handleCardKeyDown(e, index, field)}
                                    role="radio"
                                    aria-checked={field.value === card.id}
                                    aria-label={card.ariaLabel}
                                    aria-labelledby={card.ariaLabelledBy}
                                    tabIndex={field.value === card.id ? 0 : -1}
                                >
                                    {card.content}
                                </StyledCardItem>

                                {card.label && card.label}
                            </StyledCardItemWrapper>
                        ))}
                    </StyledGrid>
                )}
            />
        </div>
    );
};

export default CardRadio;
